import React from "react";
import { connect } from "react-redux";
import { Redirect } from "react-router";
import {
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Alert, AlertTitle } from "@mui/lab";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import splashScreen from "../assets/images/login-splash.jpg";
import IconAms from "../assets/images/icon-AMS.svg";
import ptsLogo from "../assets/images/PTS-logo.png";
import { notify } from "../reducers/NotifierReducer";
import { submitLoginForm, resetPass } from "../reducers/UserReducer";
import { passValidationErrors } from "../utils/functions";
import { checkForUpdates } from "../reducers/AppInfoReducer";
import { useLocation } from 'react-router-dom';
import { RowInner } from "../utils/formStyles";

const useStyles = makeStyles((theme) => ({
  pageWrap: {
    width: "100%",
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundImage: `url(${splashScreen})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  card: {
    width: 480,
    maxWidth: "95%",
    background: "fff",
    "& img": {
      width: "100%",
    },
  },
  content: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  branding: {
    width: "100%",
    height: 200,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#52134E",
    "& img": {
      width: 150,
      height: "auto",
    },
  },
  branding2: {
    width: "100%",
    textAlign: "center",
    padding: "25px 0 5px",
    "& img": {
      height: 50,
      width: "auto",
    },
  },
  actions: {
    textAlign: "center",
    marginTop: 30,
    '& .btn': {
      background: "#52134E",
      '&:hover': {
        background: "#9D5FCC",
      },
    },
    "& button": {
      paddingLeft: "4em",
      paddingRight: "4em",
      textTransform: "uppercase",
    },
  },
  passReset: {
    textAlign: "right",
    "& span": {
      fontSize: 13,
      color: "#808080",
      cursor: "pointer",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
  version: {
    position: "absolute",
    bottom: "0.5em",
    left: "1em",
    lineHeight: 1,
  },
  invalid: {
    textAlign: "center",
    fontSize: "1.5em",
    margin: "2em 0",
  },
  alert: {
    marginBottom: 32,
    "&.MuiAlert-standardError": {
      color: "rgb(84, 18, 18)",
      backgroundColor: "rgb(250, 234, 234)",
    },
  },
  textField: {
    width: '100% !important',
  },
  loader: {
    position: 'absolute',
    top: '-8px',
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'rgba(255, 255, 255, 0.6)',
    zIndex: 10,
  },
  formWrap: {
    marginBottom: 16,
    '& .MuiInputBase-root': {
      color: '#333',
    },
    '& .MuiFormLabel-root': {
      color: '#999',
      '&.MuiInputLabel-shrink': {
        color: theme.palette.primary.main,
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '& .MuiOutlinedInput-input:-webkit-autofill': {
      boxShadow: 'none',
      textFillColor: 'inherit',
    },
    '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const PageResetPassword = (props) => {
  const classes = useStyles();
  const { appVersion } = props.appInfo;
  const { user, code } = useLocation().state || {};
  const [username, setUsername] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [newPass, setNewPass] = React.useState("");
  const [confirmNewPass, setConfirmNewPass] = React.useState("");
  const [error, setError] = React.useState(false);
  const [errors, setErrors] = React.useState([]);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const disabled = Boolean(
    error || !newPass || loading || newPass !== confirmNewPass
  );
  const repeatPassErr = newPass === confirmNewPass ? false : "Passwords don't match";
  const [goToLogin, setGoToLogin] = React.useState(false);
  const [goToHome, setGoToHome] = React.useState(false);

  React.useEffect(() => {
    props.checkForUpdates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  React.useEffect(() => {
    if (!user) {
      setGoToLogin(true);
      return;
    }
    setUsername(user);
  }, [user]);

  React.useEffect(() => {
    const validationErrors = passValidationErrors("", newPass);
    if (newPass && validationErrors.length) {
      setError(validationErrors[0]);
    } else {
      if (error) setError(false);
    }
    // eslint-disable-next-line
  }, [newPass]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const resetPassword = async () => {
    setLoading(true);
    try {
      let result = await resetPass(code, newPass, username);
      props.notify("Password updated!", "success");
      if (result === 'TFA') {
        setGoToLogin(true);
      } else {
        await props.submitLoginForm(username, newPass);
        setGoToHome(true);
      }
    } catch (err) {
      if (err.errors && err.errors.length) {
        setErrors(err.errors);
      } else {
        const message = err.message ? err.message : "Error resetting password";
        setErrors([message]);
      }
    }
    setLoading(false);
  };


  const redirectToLogin = async () => {
    setGoToLogin(true);
  };

  const renderPasswordRestForm = () => {
    return (
      <>
        <div style={{ position: 'relative' }}>
          {loading && (
            <div className={classes.loader}>
              <CircularProgress />
            </div>
          )}
          <RowInner className={classes.formWrap}>
            <TextField
              className={classes.textField}
              label="Username"
              value={username}
              onChange={(e) => setUsername(e?.target?.value)}
              required
              variant="outlined"
              placeholder="Enter your username"
              type="text"
              disabled={true}
            />
          </RowInner>
          <RowInner className={classes.formWrap}>
            <TextField
              className={classes.textField}
              label="New Password"
              value={newPass}
              onChange={(e) => setNewPass(e?.target?.value)}
              type={showPassword ? "text" : "password"}
              error={error}
              helperText={error}
              placeholder="Enter new password*"
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </RowInner>
          <RowInner className={classes.formWrap}>
            <TextField
              className={classes.textField}
              label="Repeat New Password"
              value={confirmNewPass}
              onChange={(e) => setConfirmNewPass(e?.target?.value)}
              type={showConfirmPassword ? "text" : "password"}
              error={repeatPassErr}
              helperText={repeatPassErr}
              placeholder="Confirm password*"
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowConfirmPassword}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </RowInner>
          <RowInner className={`${classes.actions} d-flex justify-content-between`} style={{ paddingRight: '32px', paddingLeft: '32px'}}>
            <Button variant="outlined" autoFocus size="large" onClick={redirectToLogin}>
              Cancel
            </Button>
            <Button
              className="btn"
              color="primary"
              variant="contained"
              size="large"
              autoFocus
              onClick={resetPassword}
              disabled={disabled}>
              Continue
            </Button>
          </RowInner>
        </div>
      </>
    );
  };

  if (goToLogin) return <Redirect to="/" />;
  else if (goToHome) return <Redirect to="/home" />;

  return (
    <div className={classes.pageWrap}>
      <Card className={classes.card}>
        <div className={classes.branding}>
          <img src={IconAms} alt="AMS icon" />
        </div>
        <div className={classes.branding2}>
          <img src={ptsLogo} alt="PTS logo" />
        </div>
        <CardContent className="mb-3">
          {errors.length > 0 && (
            <Alert severity="error" className={classes.alert}>
              <AlertTitle>Password validation errors</AlertTitle>
              <ul>
                {errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </Alert>
          )}
          {renderPasswordRestForm()}

          {/* <p style={{ fontWeight: 'bold' }}> v. {appVersion}</p> */}
          <div className={classes.version}> v. {appVersion}</div>
        </CardContent>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  themeMode: state.theme.mode,
  appInfo: state.appInfo,
  websocket: state.websocket,
});

export default connect(mapStateToProps, {
  notify,
  checkForUpdates,
  submitLoginForm,
})(PageResetPassword);
